import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Check, X, Calendar, Search, AlertTriangle, RefreshCw, UserPlus, MessageSquare, Clock, Play } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookingApi } from '@/lib/bookingApi';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const MEL_TZ = 'Australia/Melbourne';
const fmtBoth = (iso) => {
  try {
    const d = new Date(iso);
    const local = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    const mel = new Intl.DateTimeFormat('en-AU', { timeZone: MEL_TZ, weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    return { local, mel };
  } catch (_e) { return { local: iso, mel: iso }; }
};

const STATUS_FILTERS = [
  { v: 'pending', l: 'Pending', cls: 'bg-amber-100 text-amber-800' },
  { v: 'approved', l: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  { v: 'declined', l: 'Declined', cls: 'bg-red-100 text-red-800' },
  { v: '', l: 'All', cls: 'bg-gray-100 text-gray-700' },
];

const STATUS_BADGES = {
  pending: 'bg-amber-100 text-amber-800',
  pending_matching: 'bg-fuchsia-100 text-fuchsia-800',
  pending_tutor_time: 'bg-indigo-100 text-indigo-800',
  approved: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  scheduled: 'bg-blue-100 text-blue-800',
  live: 'bg-emerald-500 text-white animate-pulse',
  completed: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
  missed: 'bg-orange-100 text-orange-800',
};

const STATUS_LABEL = {
  pending: 'pending',
  pending_matching: '✨ Needs Tutor Match',
  pending_tutor_time: '⏱ Needs Time Match',
  approved: 'approved',
  declined: 'declined',
};

const Card = ({ b, onApprove, onDecline, onForce, onReassign, busy }) => {
  const t = b.start_time ? fmtBoth(b.start_time) : { local: 'To be scheduled', mel: 'To be scheduled' };
  const isMatching = b.request_status === 'pending_matching';
  const isTutorTime = b.request_status === 'pending_tutor_time';
  const showStandardActions = b.request_status === 'pending';
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`bk-card-${b.booking_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold text-gray-900">{b.student_name}</div>
            <span className="text-[10px] text-gray-400">→</span>
            <div className="text-sm font-bold text-gray-900">{b.tutor_name || '(to be assigned)'}</div>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGES[b.request_status] || 'bg-gray-100 text-gray-700'}`} data-testid={`bk-status-${b.booking_id}`}>
              {STATUS_LABEL[b.request_status] || b.request_status}
            </span>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGES[b.status] || 'bg-gray-100 text-gray-700'}`}>{b.status}</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5 flex gap-3 flex-wrap">
            <span><strong>{b.subject}</strong></span>
            <span>{b.program}</span>
            <span>{b.lesson_type}</span>
            <span>{b.duration_min} min</span>
          </div>
          <div className="text-[11px] text-gray-700 mt-1.5 flex gap-3 flex-wrap">
            <span><Clock className="h-3 w-3 inline mr-1 text-gray-400" /><strong>Your local:</strong> {t.local}</span>
            <span><strong>Melbourne:</strong> {t.mel}</span>
          </div>
          {b.preferred_window && <div className="text-[11px] text-gray-700 mt-1.5"><strong>Preferred:</strong> {b.preferred_window}</div>}
          {b.student_notes && <div className="text-[11px] text-gray-600 mt-2 p-2 bg-gray-50 rounded">"{b.student_notes}"</div>}
          {b.tutor_response_notes && <div className="text-[11px] text-red-700 mt-2 p-2 bg-red-50 rounded">Tutor: {b.tutor_response_notes}</div>}
          {b.manual_override && <div className="text-[11px] text-purple-700 mt-2 p-2 bg-purple-50 rounded">Override by {b.manual_override.actor}: {b.manual_override.reason || `→ ${b.manual_override.kind || 'force'}`}</div>}
          {(isMatching || isTutorTime) && (
            <div className="text-[11px] text-fuchsia-800 bg-fuchsia-50 border border-fuchsia-100 rounded p-2 mt-2 flex items-start gap-2">
              <UserPlus className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                {isMatching ? 'Use "Reassign" to pick a tutor AND set a date/time. The booking will move to Approved automatically.' :
                              'Tutor needs to propose a time. Use "Reassign" (same tutor) to set the date/time and approve, or change tutor + time at once.'}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid={`bk-actions-${b.booking_id}`}>
          {showStandardActions && (
            <>
              <button onClick={() => onApprove(b)} disabled={busy} className="h-8 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40" data-testid={`bk-approve-${b.booking_id}`}><Check className="h-3 w-3" /> Approve</button>
              <button onClick={() => onDecline(b)} disabled={busy} className="h-8 px-3 rounded-full bg-white border border-red-300 text-red-700 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40" data-testid={`bk-decline-${b.booking_id}`}><X className="h-3 w-3" /> Decline</button>
            </>
          )}
          {(isMatching || isTutorTime) && (
            <button onClick={() => onDecline(b)} disabled={busy} className="h-8 px-3 rounded-full bg-white border border-red-300 text-red-700 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40" data-testid={`bk-decline-${b.booking_id}`}><X className="h-3 w-3" /> Decline</button>
          )}
          <button onClick={() => onReassign(b)} disabled={busy} className={`h-8 px-3 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40 ${isMatching || isTutorTime ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-blue-50 hover:text-blue-700'}`} data-testid={`bk-reassign-${b.booking_id}`}>
            <RefreshCw className="h-3 w-3" /> {isMatching ? 'Assign tutor + time' : isTutorTime ? 'Set time' : 'Reassign'}
          </button>
          <button onClick={() => onForce(b)} disabled={busy} className="h-8 px-3 rounded-full bg-white border border-purple-300 text-purple-700 hover:bg-purple-50 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 disabled:opacity-40" data-testid={`bk-force-${b.booking_id}`}><AlertTriangle className="h-3 w-3" /> Force status</button>
        </div>
      </div>
    </div>
  );
};

const ForceModal = ({ open, onClose, onConfirm }) => {
  const [status, setStatus] = useState('completed');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStatus('completed'); setReason(''); } }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="force-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="text-sm font-bold text-gray-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-purple-700" /> Admin emergency override</div>
        <div className="text-[11px] text-gray-500 mt-1">Use only if the automation failed.</div>
        <div className="mt-4 space-y-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" data-testid="force-status">
            <option value="completed">Mark completed</option>
            <option value="cancelled">Mark cancelled</option>
            <option value="missed">Mark missed</option>
          </select>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (logged)" className="w-full p-2 rounded-lg border border-gray-200 text-xs" data-testid="force-reason" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm({ status, reason }); } finally { setBusy(false); } }} className="h-9 px-5 rounded-full bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="force-confirm">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />} Confirm override</button>
        </div>
      </div>
    </div>
  );
};

const ReassignModal = ({ open, booking, onClose, onConfirm }) => {
  const [tutors, setTutors] = useState([]);
  const [tutorEmail, setTutorEmail] = useState('');
  const [reason, setReason] = useState('');
  const [newTimeLocal, setNewTimeLocal] = useState(''); // datetime-local string in browser local TZ
  const [busy, setBusy] = useState(false);
  const isMatching = booking?.request_status === 'pending_matching';
  const isTutorTime = booking?.request_status === 'pending_tutor_time';
  const needsTime = isMatching || isTutorTime;
  useEffect(() => {
    if (!open) return;
    setReason(''); setTutorEmail(''); setNewTimeLocal('');
    bookingApi.listTutors().then((r) => {
      const list = (r.items || []);
      // For pending_matching the original tutor is null — show ALL tutors.
      // For pending_tutor_time keep the original tutor in the list (admin may just set the time).
      setTutors(list);
      // Pre-fill tutor for pending_tutor_time
      if (booking?.tutor_email && isTutorTime) setTutorEmail(booking.tutor_email);
    }).catch(() => {});
  }, [open, booking, isTutorTime]);
  if (!open) return null;
  // Convert datetime-local (browser local TZ) → ISO-8601 UTC for the API
  const toIso = (local) => {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="reassign-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="text-sm font-bold text-gray-900 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-blue-700" />
          {isMatching ? 'Match tutor + time' : isTutorTime ? 'Set time for tutor' : 'Reassign tutor'}
        </div>
        <div className="text-[11px] text-gray-500 mt-1">
          {isMatching ? 'Pick a tutor and set the lesson date/time. The booking will move to Approved.' :
           isTutorTime ? 'Set the lesson date/time. You can also change the tutor if needed.' :
                         'Move this booking to a different tutor (and optionally a new time).'}
        </div>
        <div className="mt-4 space-y-3">
          <select value={tutorEmail} onChange={(e) => setTutorEmail(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" data-testid="reassign-tutor">
            <option value="">— pick a tutor —</option>
            {tutors.map((t) => <option key={t.tutor_email} value={t.tutor_email}>{t.tutor_name} ({t.tutor_email})</option>)}
          </select>
          {needsTime && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Lesson date & time {isMatching && <span className="text-red-700">*</span>} <span className="font-normal text-gray-400">(your local TZ → converted to UTC)</span></div>
              <input type="datetime-local" value={newTimeLocal} onChange={(e) => setNewTimeLocal(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm" data-testid="reassign-time" />
            </div>
          )}
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (logged)" className="w-full h-10 px-3 rounded-lg border border-gray-200 text-xs" data-testid="reassign-reason" />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-4 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider">Cancel</button>
          <button disabled={busy || !tutorEmail || (needsTime && !newTimeLocal)} onClick={async () => {
            setBusy(true);
            try { await onConfirm({ tutor_email: tutorEmail, reason, new_start_time: toIso(newTimeLocal) }); }
            finally { setBusy(false); }
          }} className="h-9 px-5 rounded-full bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="reassign-confirm">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {isMatching || isTutorTime ? 'Confirm booking' : 'Reassign'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminBookingRequestsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [status, setStatus] = useState('pending');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [running, setRunning] = useState(false);
  const [forceOpen, setForceOpen] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await ax.get('/api/booking/admin/booking-requests', { params: { status: status || undefined, q: search || undefined } }); setItems(r.data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load bookings.'); }
    finally { setBusy(false); }
  }, [status, search]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/admin/login', { replace: true }); return; }
    if (!['admin', 'owner'].includes((user.role || '').toLowerCase())) { navigate('/', { replace: true }); return; }
    load();
  }, [loading, user, navigate, load]);

  const approve = async (b) => { try { await bookingApi.respond(b.booking_id, { action: 'approve' }); toast.success('Approved'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const decline = async (b) => { const reason = window.prompt('Reason (optional, sent to student):'); try { await bookingApi.respond(b.booking_id, { action: 'decline', reason }); toast.success('Declined'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const force = async ({ status: s, reason }) => { try { await ax.post(`/api/admin/bookings/${forceOpen.booking_id}/force-status`, { status: s, reason }); toast.success(`Status forced to ${s}`); setForceOpen(null); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const reassign = async ({ tutor_email, reason, new_start_time }) => {
    try {
      await ax.post(`/api/booking/admin/bookings/${reassignOpen.booking_id}/reassign`, { tutor_email, reason, new_start_time });
      toast.success(reassignOpen.request_status === 'pending_matching' || reassignOpen.request_status === 'pending_tutor_time' ? 'Booking confirmed' : 'Reassigned');
      setReassignOpen(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const runReminders = async () => { setRunning(true); try { const r = await ax.post('/api/admin/run-reminders'); toast.success(`Reminders: 24h=${r.data.r24.sent}, 1h=${r.data.r1.sent}`); } catch (e) { toast.error('Failed'); } finally { setRunning(false); } };
  const runSweep = async () => { setRunning(true); try { const r = await ax.post('/api/admin/run-status-sweep'); toast.success(`Swept — completed:${r.data.completed} missed:${r.data.missed}`); load(); } catch (e) { toast.error('Failed'); } finally { setRunning(false); } };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-bookings-page">
      <header className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE ADMIN</div>
            <div className="text-sm font-bold">Booking Requests</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runReminders} disabled={running} className="h-9 px-3 rounded-full bg-[#D3D86F] hover:bg-[#c4ca54] text-black text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="run-reminders"><Play className="h-3 w-3" /> Run reminders</button>
            <button onClick={runSweep} disabled={running} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="run-sweep"><RefreshCw className="h-3 w-3" /> Sweep statuses</button>
            <button onClick={() => navigate('/admin/dashboard')} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5"><ArrowLeft className="h-3 w-3" /> Admin</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap" data-testid="status-tabs">
            {STATUS_FILTERS.map((f) => (
              <button key={f.v} onClick={() => setStatus(f.v)} data-testid={`tab-${f.v || 'all'}`}
                className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider transition ${status === f.v ? 'bg-black text-white' : `${f.cls} hover:opacity-80`}`}>
                {f.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 h-9">
            <Search className="h-3.5 w-3.5 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Tutor / student / program…" className="bg-transparent text-xs focus:outline-none w-44 sm:w-64" data-testid="search-input" />
          </div>
        </div>

        {busy ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-2xl text-center text-xs text-gray-400 italic py-12" data-testid="empty">No bookings match.</div>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {items.map((b) => <Card key={b.booking_id} b={b} onApprove={approve} onDecline={decline} onForce={(x) => setForceOpen(x)} onReassign={(x) => setReassignOpen(x)} />)}
          </div>
        )}
      </main>

      <ForceModal open={!!forceOpen} onClose={() => setForceOpen(null)} onConfirm={force} />
      <ReassignModal open={!!reassignOpen} booking={reassignOpen} onClose={() => setReassignOpen(null)} onConfirm={reassign} />
    </div>
  );
};

export default AdminBookingRequestsPage;
